/**
 * Minimal i18n module for bim-cadaplus-app.
 *
 * Features:
 * - Nested key lookup (max 2 levels: 'namespace.key')
 * - {{variable}} interpolation
 * - Polish plural rules (Unicode CLDR):
 *     _one  — n === 1
 *     _few  — n % 10 in 2-4 AND n % 100 not in 12-14
 *     _many — all other cases
 *     _other — English fallback (n !== 1)
 * - Locale fallback chain: active locale → pl_PL → key itself (visible in dev)
 *
 * Usage:
 *   i18n.init('pl_PL', { catalog: { no_results: 'Brak wyników' } })
 *   i18n.t('catalog.no_results')
 *   i18n.t('catalog.results_count', { count: 5 })
 *   i18n.plural('catalog.results_count', 5, { count: 5 })
 */

class I18n {
  #locale = 'pl_PL'
  #translations = {}
  #fallback = {}

  /**
   * Initialises the i18n instance with a locale and translations map.
   * Call once in main.jsx before rendering. Safe to call multiple times (re-init).
   *
   * @param {string} locale - WP locale string, e.g. 'pl_PL' or 'en_US'
   * @param {Record<string, object>} translations - Nested translation map for active locale
   * @param {Record<string, object>} [fallbackTranslations] - Fallback map (pl_PL) used when key missing in active locale
   */
  init(locale, translations, fallbackTranslations = {}) {
    this.#locale = locale
    this.#translations = translations
    this.#fallback = fallbackTranslations
  }

  /**
   * Returns the current locale.
   * @returns {string}
   */
  get locale() {
    return this.#locale
  }

  /**
   * Translates a dot-notation key with optional {{variable}} interpolation.
   *
   * If `vars.count` is provided, automatically selects the correct plural form
   * (same behaviour as i18next) — looks for key_one / key_few / key_many first,
   * falls back to base key when no plural variants exist.
   *
   * @param {string} key - Dot-notation key, e.g. 'catalog.no_results'
   * @param {Record<string, string|number>} [vars] - Interpolation variables
   * @returns {string}
   */
  t(key, vars = {}) {
    if (vars.count !== undefined) {
      return this.plural(key, vars.count, vars)
    }
    const raw = this.#resolve(key)
    return this.#interpolate(raw, vars)
  }

  /**
   * Translates a pluralisable key by appending the correct plural suffix.
   * Looks up `key_one`, `key_few`, `key_many` (or `key_other` for non-Polish locales).
   *
   * @param {string} key - Base dot-notation key without plural suffix, e.g. 'catalog.results_count'
   * @param {number} count - The count that determines which plural form to use
   * @param {Record<string, string|number>} [vars] - Interpolation variables (count is auto-included)
   * @returns {string}
   */
  plural(key, count, vars = {}) {
    const suffix = this.#pluralSuffix(count)
    const raw = this.#resolve(`${key}_${suffix}`) ?? this.#resolve(`${key}_other`) ?? this.#resolve(key)
    return this.#interpolate(raw, { count, ...vars })
  }

  // ---------------------------------------------------------------------------

  /**
   * Resolves a dot-notation key against active translations, then fallback.
   * Returns the key string itself when not found anywhere (visible in dev).
   *
   * @param {string} key
   * @returns {string}
   */
  #resolve(key) {
    return this.#lookup(this.#translations, key)
      ?? this.#lookup(this.#fallback, key)
      ?? key
  }

  /**
   * Walks a nested object by dot-notation path.
   *
   * @param {object} obj
   * @param {string} key
   * @returns {string|undefined}
   */
  #lookup(obj, key) {
    const parts = key.split('.')
    let current = obj
    for (const part of parts) {
      if (current == null || typeof current !== 'object') return undefined
      current = current[part]
    }
    return typeof current === 'string' ? current : undefined
  }

  /**
   * Replaces {{variable}} placeholders with values from vars map.
   *
   * @param {string} str
   * @param {Record<string, string|number>} vars
   * @returns {string}
   */
  #interpolate(str, vars) {
    if (!vars || typeof str !== 'string') return str
    return str.replace(/\{\{(\w+)\}\}/g, (_, name) =>
      name in vars ? String(vars[name]) : `{{${name}}}`
    )
  }

  /**
   * Returns the plural suffix for a given count based on current locale rules.
   * Polish: _one / _few / _many
   * Other (English-style): _one / _other
   *
   * @param {number} n
   * @returns {'one'|'few'|'many'|'other'}
   */
  #pluralSuffix(n) {
    const isPolish = this.#locale.startsWith('pl')

    if (isPolish) {
      if (n === 1) return 'one'
      if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return 'few'
      return 'many'
    }

    // English and other locales: singular / plural
    return n === 1 ? 'one' : 'other'
  }
}

/** Singleton instance — initialised once in main.jsx, consumed via useI18n(). */
export const i18n = new I18n()
